import routes from "@/router/routes-list";
import { canAccessRegionalMenu } from "@/utils/regional-user-permissions";
import { RANKINGS_NACIONAIS, podeVerRanking } from "@/utils/ranking-nacional";
import { RANKINGS_ESTADUAIS, RANKING_ESTADUAL_ATIVO, temRankingEstadual } from "@/utils/ranking-estadual";

/** Sub-grupos de 3º nível dentro de "Rankings", na ordem do menu. */
export const GRUPOS_RANKINGS = [
  { key: "nacional-brasil", label: "Nacional Brasil" },
  { key: "nacional-truck", label: "Nacional Truck" },
  { key: "estadual-brasil", label: "Estadual Brasil" },
  { key: "estadual-truck", label: "Estadual Truck" },
];

/**
 * Fonte única de verdade do menu lateral.
 *
 * Antes deste arquivo as regras de visibilidade viviam em dois lugares: os
 * `v-if` do template de `components/side-nav.vue` e as funções `visible()` da
 * lista de passos do `components/onboarding-modal.vue`. As duas cópias já
 * tinham divergido (o tour ignorava Informativos, Eventos, Comunidade, CRM,
 * BI Teams, Usuários, Convite APN e Leads), e um tour que ensina uma tela que
 * o usuário não tem é pior do que um tour que não ensina nada.
 *
 * Agora existe uma descrição só: cada item declara sua chave, o rótulo exibido
 * no menu, a rota (quando é navegação interna) e a condição de visibilidade
 * (módulo contratado + permissão da secretária + flags de perfil). O side-nav
 * consome isso nos `v-if`; o tour consome isso para montar os passos. As duas
 * telas não têm mais como divergir.
 *
 * IMPORTANTE: este módulo descreve apenas os itens que o usuário pode ver. Os
 * `<li class="d-none">` legados do side-nav (Teams University, Financeiro da
 * carteira, dashboards/rankings do APVS) continuam no template e ficam de fora
 * daqui de propósito — eles não são menu, são resquício.
 *
 * ATENÇÃO a uma assimetria herdada: `Comunidade`, `CRM` e `BI Teams` são os
 * únicos itens que NÃO passam por `canAccess` — eles não têm rota interna
 * (abrem por SSO/OAuth em outro sistema), logo não existem no catálogo de
 * permissões da secretária e um `canAccess` sempre daria falso, sumindo com o
 * item para toda secretária. O gate deles é só o módulo/flag, exatamente como
 * era antes desta refatoração. Quem decide de verdade continua sendo o
 * backend, que reconfere o módulo antes de emitir o ticket de SSO. Se um dia
 * esses itens ganharem rota própria, o `canAccess` deve entrar aqui também.
 *
 * Este módulo é a fonte de verdade da APRESENTAÇÃO do menu — nunca de
 * autorização. Nada aqui protege dado nenhum; a permissão real é do backend.
 */

// ---------------------------------------------------------------------------
// Predicados de base
// ---------------------------------------------------------------------------

/**
 * `meta.menu.hidden` marca a rota como NÃO oferecível na tela de permissões da
 * secretária (ver `utils/regional-user-permissions.js`). Não é, em geral, um
 * gate de visibilidade de menu — o único item que o usa assim hoje é o
 * "QR Codes" do Acessa, comportamento preservado explicitamente abaixo.
 */
export function isMenuHidden(path) {
  const route = routes.find((r) => r.path === path);
  return !!(route && route.meta && route.meta.menu && route.meta.menu.hidden);
}

export function canAccess(user, path) {
  return canAccessRegionalMenu(user, path);
}

export function moduleActive(user, name) {
  const modules = (user && user.modules) || {};
  return modules[name] === "active";
}

// Regional titular: `regional == 1` e sessão que NÃO é de secretária. A
// comparação original era frouxa (`== 1`), então normalizamos com Number para
// aceitar tanto 1 quanto "1".
function isRegionalOwner(user) {
  return !!user && Number(user.regional) === 1 && !user.regional_user;
}

// Comunidade (Circle): liberada para admin ou para quem tem a flag `circle`.
function isCircleMember(user) {
  if (!user) return false;
  return Number(user.admin) === 1 || Number(user.circle) === 1;
}

// "Indicados, Diretos, Equipe e Matriz" — usado nos textos do tour para citar
// exatamente os subitens que AQUELE usuário tem, e nenhum a mais.
export function joinLabels(labels) {
  if (!labels || labels.length === 0) return "";
  if (labels.length === 1) return labels[0];
  return labels.slice(0, -1).join(", ") + " e " + labels[labels.length - 1];
}

// ---------------------------------------------------------------------------
// Catálogo
// ---------------------------------------------------------------------------
// Ordem = ordem visual do side-nav. Cada item tem:
//   key         chave estável, usada no atributo data-tour="menu-<key>"
//   label       rótulo exibido no menu (mesmo texto do <span>)
//   path        rota interna (ausente em itens que abrem sistema externo)
//   external    true quando o item sai do app (SSO/Circle)
//   moduleGate  pré-condição de módulo/perfil de um grupo
//   hidden      true = some do side-nav e do tour para todos (não mexe em rota
//               nem em permissão; é só apresentação). Remova a flag p/ reativar
//   visible     condição do item simples
//   children    subitens de um grupo colapsável (MetisMenu)
//   groups      (opcional) 3º nível: [{ key, label }]. Cada child declara o seu
//               `group`; o side-nav desenha um sub-grupo colapsável por group
//               (ver isMenuGroupVisible). Usado pelo "Rankings" (Nacional/Estadual × Brasil/Truck).
//   description texto do passo do tour (string ou função(ctx))
const MENU_ITEMS = [
  {
    key: "home",
    label: "Dashboard",
    path: "/",
    visible: (user) => canAccess(user, "/"),
    description:
      "A tela inicial reúne seus números do momento: produção, indicadores e avisos importantes. É o melhor lugar para começar o dia.",
  },
  {
    // Só para quem tem APVS (Brasil ou Truck). Secretária vê se tiver a permissão da tela.
    key: "minhas-metas",
    label: "Minhas Metas",
    path: "/minhas-metas",
    visible: (user) =>
      canAccess(user, "/minhas-metas") &&
      (moduleActive(user, "apvs_brasil") || moduleActive(user, "apvs_truck")),
    description:
      "Em Minhas Metas você define quantas adesões quer trazer por mês e acompanha o que já conta para o próximo fechamento, as placas que ainda não pagaram o 1º boleto e o prazo de cada uma.",
  },
  {
    // Cartão de visita de conquistas: pessoal da titular (nunca para secretária) e só com APVS.
    key: "meu-cartao",
    label: "Meu Cartão",
    hidden: true, // oculto temporariamente a pedido do usuário — remover esta linha para reativar (rota /meu-cartao segue acessível por URL)
    path: "/meu-cartao",
    visible: (user) =>
      !!user && !user.regional_user &&
      (moduleActive(user, "apvs_brasil") || moduleActive(user, "apvs_truck")),
    description:
      "Em Meu Cartão você monta seu cartão de visita com foto, carreira e PINs conquistados, e baixa em imagem ou PDF para compartilhar nas redes sociais.",
  },
  {
    key: "informativos",
    label: "Informativos",
    hidden: true, // oculto a pedido do usuário — remover esta linha para reativar (rota /informatives segue acessível por URL)
    path: "/informatives",
    visible: (user) => canAccess(user, "/informatives"),
    description:
      "Os comunicados oficiais da empresa ficam aqui: mudanças de campanha, avisos de pagamento e novidades. Vale conferir sempre que aparecer algo novo.",
  },
  {
    key: "eventos",
    label: "Eventos",
    hidden: true, // oculto a pedido do usuário — remover esta linha para reativar (rota /events segue acessível por URL)
    path: "/events",
    visible: (user) => canAccess(user, "/events"),
    description:
      "Convenções, treinamentos e encontros da rede aparecem em Eventos, com data, local e as instruções de participação.",
  },
  {
    key: "comunidade",
    label: "Comunidade",
    external: true,
    visible: (user) => isCircleMember(user),
    description: (ctx) =>
      ctx.embedded
        ? "A Comunidade é o espaço de troca com outros consultores. Dentro do aplicativo ela abre em uma janela aqui mesmo, sem tirar você do sistema."
        : "A Comunidade é o espaço de troca com outros consultores. Ela abre em uma nova aba do navegador, já com o seu login.",
  },
  {
    key: "crm",
    label: "CRM",
    external: true,
    visible: (user) => moduleActive(user, "teamscrm") || moduleActive(user, "crmconsultor"),
    description:
      "O CRM é onde você organiza seus contatos e o funil de vendas. O acesso é automático: clicando aqui você entra já logado, sem digitar senha de novo.",
  },
  {
    key: "usuarios",
    label: "Usuários",
    path: "/usuarios",
    visible: (user) => isRegionalOwner(user),
    description:
      "Como Regional, é aqui que você cadastra as secretárias do seu escritório e escolhe, uma a uma, quais telas cada uma pode abrir.",
  },
  {
    key: "apn",
    label: "Convite APN",
    path: "/apn",
    // `!isMenuHidden('/apn')` é herança do template original; hoje a rota tem
    // `hidden: false`, então o gate é inócuo. Mantido para não mudar
    // comportamento caso alguém marque a rota como oculta.
    visible: (user) =>
      !isMenuHidden("/apn") && canAccess(user, "/apn") && moduleActive(user, "apn"),
    description:
      "Em Convite APN você gera e acompanha os convites de cadastro. Cada convite vira um link próprio para enviar ao indicado.",
  },
  {
    key: "biteams",
    label: "BI Teams",
    external: true,
    visible: (user) => moduleActive(user, "biteams"),
    description:
      "O BI Teams reúne os painéis de ranking e desempenho da rede. Assim como o CRM, ele abre já com o seu login.",
  },
  {
    key: "rede",
    label: "Rede",
    children: [
      {
        key: "indicados",
        label: "Indicados",
        path: "/network/indicados",
        visible: (user) => canAccess(user, "/network/indicados"),
      },
      {
        key: "diretos",
        label: "Diretos",
        path: "/network/diretos",
        visible: (user) => canAccess(user, "/network/diretos"),
      },
      {
        key: "equipe",
        label: "Equipe",
        path: "/network/equipe",
        visible: (user) => canAccess(user, "/network/equipe"),
      },
      {
        key: "matriz",
        label: "Matriz",
        path: "/network/matriz",
        visible: (user) => canAccess(user, "/network/matriz"),
      },
    ],
    description: (ctx) =>
      "Clicando em Rede o menu se abre e mostra " +
      joinLabels(ctx.childLabels) +
      ". É onde você acompanha quem você trouxe e como sua estrutura está crescendo.",
  },
  {
    key: "apvs-brasil",
    label: "APVS Brasil",
    moduleGate: (user) => moduleActive(user, "apvs_brasil"),
    children: [
      {
        key: "adesoes",
        label: "Adesões",
        path: "/apvs/brasil/adesoes",
        visible: (user) => canAccess(user, "/apvs/brasil/adesoes"),
      },
      {
        key: "metas",
        label: "Metas",
        path: "/apvs/brasil/metas",
        visible: (user) => canAccess(user, "/apvs/brasil/metas"),
      },
      {
        key: "recorrente",
        label: "Recorrente",
        path: "/apvs/brasil/recorrente",
        visible: (user) => canAccess(user, "/apvs/brasil/recorrente"),
      },
      {
        key: "comissionamento",
        label: "Vitalício",
        path: "/apvs/brasil/comissionamento",
        visible: (user) => canAccess(user, "/apvs/brasil/comissionamento"),
      },
      {
        key: "rastreador",
        label: "Rastreador",
        path: "/apvs/brasil/rastreador",
        visible: (user) => canAccess(user, "/apvs/brasil/rastreador"),
      },
    ],
    description: (ctx) =>
      "Aqui fica a sua produção do APVS Brasil: " +
      joinLabels(ctx.childLabels) +
      ". Os números vêm do processamento oficial e são atualizados ao longo do dia.",
  },
  {
    key: "apvs-truck",
    label: "APVS Truck",
    moduleGate: (user) => moduleActive(user, "apvs_truck"),
    children: [
      {
        key: "adesoes",
        label: "Adesões",
        path: "/apvs/truck/adesoes",
        visible: (user) => canAccess(user, "/apvs/truck/adesoes"),
      },
      {
        key: "metas",
        label: "Metas",
        path: "/apvs/truck/metas",
        visible: (user) => canAccess(user, "/apvs/truck/metas"),
      },
      {
        key: "recorrente",
        label: "Recorrente",
        path: "/apvs/truck/recorrente",
        visible: (user) => canAccess(user, "/apvs/truck/recorrente"),
      },
      {
        key: "comissionamento",
        label: "Vitalício",
        path: "/apvs/truck/comissionamento",
        visible: (user) => canAccess(user, "/apvs/truck/comissionamento"),
      },
      {
        key: "rastreador",
        label: "Rastreador",
        path: "/apvs/truck/rastreador",
        visible: (user) => canAccess(user, "/apvs/truck/rastreador"),
      },
    ],
    description: (ctx) =>
      "Mesma estrutura da seção anterior, só que para o APVS Truck: " +
      joinLabels(ctx.childLabels) +
      ".",
  },
  {
    // Sem `moduleGate` de propósito: o pedido não vinculou os rankings a nenhum módulo
    // contratado. Quem não é da trilha abre a tela e vê o motivo em "Sua posição"; para a
    // secretária, cada subitem depende da permissão correspondente.
    //
    // Árvore: Rankings > Regras e Regulamentos + 4 sub-grupos (Nacional Brasil/Truck, Estadual
    // Brasil/Truck) > rankings. Os itens do novo modelo de reconhecimento (Regras, Consultores, …)
    // seguem ocultos com `hidden: true` no próprio subitem — remover a flag para reativar (rotas
    // /biteams/rk-* seguem acessíveis por URL).
    //
    // Ranking Nacional: porte de Rankings > Ranking Nacional do biTeams, mesmos rótulos.
    // Ranking Estadual: os mesmos rankings só com a diretoria de quem está logado — só aparece se
    // o usuário pertence a uma diretoria (a API decide, via biteams/rk-estadual-contexto; ver
    // utils/ranking-estadual.js). Para a secretária, cada subitem depende da permissão dele.
    key: "rankings",
    label: "Rankings",
    groups: GRUPOS_RANKINGS,
    children: [
      {
        // Visível para quem vê algum ranking do Nacional ou do Estadual.
        key: "regulamento",
        label: "Regras e Regulamentos",
        path: "/biteams/rk-regulamento",
        visible: (user) =>
          RANKINGS_NACIONAIS.some((r) => canAccess(user, r.path)) ||
          (temRankingEstadual() && RANKINGS_ESTADUAIS.some((r) => canAccess(user, r.path))),
      },
      {
        key: "regras",
        label: "Regras",
        path: "/biteams/rk-regras",
        hidden: true,
        visible: (user) => canAccess(user, "/biteams/rk-regras"),
      },
      {
        key: "consultores",
        label: "Consultores",
        path: "/biteams/rk-consultores",
        hidden: true,
        visible: (user) => canAccess(user, "/biteams/rk-consultores"),
      },
      {
        key: "home",
        label: "Home Office",
        path: "/biteams/rk-home",
        hidden: true,
        visible: (user) => canAccess(user, "/biteams/rk-home"),
      },
      {
        key: "executiva",
        label: "Executiva",
        path: "/biteams/rk-executiva",
        hidden: true,
        visible: (user) => canAccess(user, "/biteams/rk-executiva"),
      },
      {
        key: "gerentes",
        label: "Gerentes",
        path: "/biteams/rk-gerentes",
        hidden: true,
        visible: (user) => canAccess(user, "/biteams/rk-gerentes"),
      },
      ...RANKINGS_NACIONAIS.map((r) => ({
        key: r.id,
        label: r.menuLabel,
        group: `nacional-${r.grupo}`,
        path: r.path,
        visible: (user) => podeVerRanking(user, r),
      })),
      ...RANKINGS_ESTADUAIS.map((r) => ({
        key: r.id,
        label: r.menuLabel,
        group: `estadual-${r.grupo}`,
        path: r.path,
        visible: (user) => temRankingEstadual() && podeVerRanking(user, r),
      })),
    ],
    description: () =>
      `Em Rankings você acompanha o Ranking Nacional${RANKING_ESTADUAL_ATIVO ? " e o Ranking Estadual" : ""} de Brasil e Truck, vê a sua posição no período e encontra as Regras e Regulamentos com o passo a passo para conferir o seu resultado no i4pro.`,
  },
  {
    key: "afiliados",
    label: "Afiliados",
    moduleGate: (user) => moduleActive(user, "afiliados"),
    children: [
      {
        key: "lista",
        label: "Meus Afiliados",
        path: "/afiliados/lista",
        visible: (user) => canAccess(user, "/afiliados/lista"),
      },
      {
        key: "leads",
        label: "Leads Indicados",
        path: "/afiliados/leads",
        visible: (user) => canAccess(user, "/afiliados/leads"),
      },
      {
        key: "convertidos",
        label: "Leads Convertidos",
        path: "/afiliados/convertidos",
        visible: (user) => canAccess(user, "/afiliados/convertidos"),
      },
      {
        key: "ranking",
        label: "Ranking",
        path: "/afiliados/ranking",
        visible: (user) => canAccess(user, "/afiliados/ranking"),
      },
    ],
    description: (ctx) =>
      "No programa de Afiliados você cadastra parceiros e acompanha o que eles indicam: " +
      joinLabels(ctx.childLabels) +
      ". Na primeira visita o regulamento do programa é apresentado para você aceitar.",
  },
  {
    key: "acessa",
    label: "Acessa",
    tourTitle: "Acessa Benefícios",
    moduleGate: (user) => moduleActive(user, "acessa"),
    children: [
      {
        key: "leads",
        label: "Leads",
        path: "/acessa/leads",
        visible: (user) => canAccess(user, "/acessa/leads"),
      },
      {
        key: "clientes",
        label: "Clientes",
        path: "/acessa/clientes",
        visible: (user) => canAccess(user, "/acessa/clientes"),
      },
      {
        key: "qrcodes",
        label: "QR Codes",
        path: "/acessa/qrcodes",
        // Uniformizado com os irmãos (ganhou o `canAccess` que faltava), mas o
        // gate de `meta.menu.hidden` do template original foi PRESERVADO: a
        // rota `/acessa/qrcodes` está marcada como `hidden: true`, então este
        // item nunca chegou a ser exibido em produção. Tirar o gate aqui
        // ligaria uma tela nova para todo mundo sem ninguém ter pedido — a
        // decisão de publicá-la é do negócio, não desta refatoração.
        visible: (user) =>
          !isMenuHidden("/acessa/qrcodes") && canAccess(user, "/acessa/qrcodes"),
      },
      {
        key: "adesao",
        label: "Adesão",
        path: "/acessa/adesao",
        visible: (user) => canAccess(user, "/acessa/adesao"),
      },
      {
        key: "recorrente",
        label: "Recorrente",
        path: "/acessa/recorrente",
        visible: (user) => canAccess(user, "/acessa/recorrente"),
      },
      {
        key: "graduacao",
        label: "Graduação",
        path: "/acessa/graduacao",
        // Era o único subitem do Acessa sem nenhuma condição — e apontava para
        // uma rota que nem existia em routes-list.js (a rota foi registrada
        // junto com esta refatoração).
        visible: (user) => canAccess(user, "/acessa/graduacao"),
      },
    ],
    description: (ctx) =>
      "A seção do Acessa Benefícios reúne " +
      joinLabels(ctx.childLabels) +
      " — do primeiro contato até a recorrência do cliente.",
  },
  {
    key: "downloads",
    label: "Downloads",
    path: "/downloads",
    visible: (user) => canAccess(user, "/downloads"),
    description:
      "Materiais de apoio, artes e documentos oficiais para o seu dia a dia ficam em Downloads, separados por pasta.",
  },
  {
    key: "leads",
    label: "Leads",
    path: "/leads/leads",
    visible: (user) => canAccess(user, "/leads/leads"),
    description:
      "Em Leads você acompanha os contatos que chegaram para você, com o histórico de cada atendimento.",
  },
  {
    key: "financeiro",
    label: "Financeiro",
    children: [
      {
        key: "apvsbrasil",
        label: "APVS Brasil",
        path: "/notasfiscais/apvsbrasil",
        visible: (user) => canAccess(user, "/notasfiscais/apvsbrasil"),
      },
      {
        key: "apvstruck",
        label: "APVS Truck",
        path: "/notasfiscais/apvstruck",
        visible: (user) => canAccess(user, "/notasfiscais/apvstruck"),
      },
    ],
    description: (ctx) =>
      "Em Financeiro você consulta e envia as notas fiscais das suas comissões, mês a mês, separadas por sistema (" +
      joinLabels(ctx.childLabels) +
      ").",
  },
  {
    key: "suporte",
    label: "Suporte",
    children: [
      {
        key: "faq",
        label: "FAQ",
        path: "/faq",
        visible: (user) => canAccess(user, "/faq"),
      },
      {
        key: "tickets",
        label: "Tickets",
        path: "/tickets",
        visible: (user) => canAccess(user, "/tickets"),
      },
    ],
    description: (ctx) =>
      "Precisou de ajuda? " +
      joinLabels(ctx.childLabels) +
      " ficam aqui: o FAQ responde as dúvidas mais comuns e, em Tickets, você abre um chamado direto para a equipe.",
  },
  {
    key: "guia",
    label: "Guia do App",
    path: "/guia-do-app",
    visible: (user) => canAccess(user, "/guia-do-app"),
    description:
      "Quer ver cada tela com calma? O Guia do App tem vídeos curtos, separados por assunto, com o passo a passo de tudo o que você acabou de ver.",
  },
];

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------

function findItem(key) {
  return MENU_ITEMS.find((item) => item.key === key) || null;
}

// Subitem com `hidden: true` some do side-nav e do tour, igual a um item de 1º nível.
function childVisible(user, child) {
  return !child.hidden && child.visible(user);
}

function itemVisible(user, item) {
  if (!item || !user) return false;
  if (item.hidden) return false;
  if (item.moduleGate && !item.moduleGate(user)) return false;

  if (item.children) {
    return item.children.some((child) => childVisible(user, child));
  }

  return item.visible(user);
}

/** Usado nos `v-if` do side-nav e para montar os passos do tour. */
export function isMenuItemVisible(user, key) {
  return itemVisible(user, findItem(key));
}

/** Usado nos `v-if` dos subitens (`<li>` dentro de `ul.sub-menu`). */
export function isMenuChildVisible(user, parentKey, childKey) {
  const parent = findItem(parentKey);
  if (!parent || !parent.children) return false;
  if (!itemVisible(user, parent)) return false;

  const child = parent.children.find((item) => item.key === childKey);
  return !!child && childVisible(user, child);
}

/** Sub-grupo de 3º nível (ex.: "Nacional Brasil" dentro de "Rankings"): visível se algum filho dele for. */
export function isMenuGroupVisible(user, parentKey, groupKey) {
  const parent = findItem(parentKey);
  if (!parent || !parent.children) return false;
  if (!itemVisible(user, parent)) return false;

  return parent.children.some((child) => child.group === groupKey && childVisible(user, child));
}

export function visibleMenuChildren(user, parentKey) {
  const parent = findItem(parentKey);
  if (!parent || !parent.children) return [];
  if (!itemVisible(user, parent)) return [];
  return parent.children.filter((child) => childVisible(user, child));
}

/**
 * Itens que aquele usuário efetivamente enxerga, na ordem do menu, já com o
 * texto do passo resolvido. `ctx.embedded` vem do `utils/webview.js` porque
 * alguns itens (Comunidade) se comportam de outro jeito dentro do app.
 */
export function visibleMenuItems(user, options) {
  const opts = options || {};

  return MENU_ITEMS.filter((item) => itemVisible(user, item)).map((item) => {
    const children = item.children ? item.children.filter((c) => childVisible(user, c)) : [];
    const ctx = {
      user,
      embedded: !!opts.embedded,
      childLabels: children.map((c) => c.label),
    };

    return {
      key: item.key,
      label: item.label,
      title: item.tourTitle || item.label,
      path: item.path || null,
      external: !!item.external,
      children,
      description:
        typeof item.description === "function" ? item.description(ctx) : item.description,
    };
  });
}

export default MENU_ITEMS;
